/** Stat model: aura mods → EffectiveStats, per-school/ability multipliers, ControlState. */
import type { AbilityId, AuraDef, CcType, ControlState, EffectiveStats, School, SimContext, StatMod, Unit, UnitId } from '../contracts';
import { clamp, unitOf } from './util';

const ZERO_STATS = { power: 0, haste: 0, crit: 0, mastery: 0, versatility: 0, armor: 0, dodge: 0, parry: 0, block: 0, blockAmount: 0, missChance: 0 };

/** CC types that stop movement, casting, swinging and commands. */
const HARD_CC: ReadonlySet<CcType> = new Set<CcType>(['stun', 'incapacitate', 'disorient']);

/** Visit every stat mod on a unit with its aura's stack count. */
export function forEachMod(ctx: SimContext, u: Unit, fn: (m: StatMod, stacks: number, def: AuraDef) => void): void {
  for (const inst of u.auras) {
    const def = ctx.content.aura(inst.auraId);
    if (!def?.mods) continue;
    // +lead (F8): ignoreStacks mods count once regardless of stacks.
    // +specs: perMissingHp mods scale with the holder's missing health fraction.
    for (const m of def.mods) fn(m, (m.ignoreStacks ? 1 : inst.stacks) * (m.perMissingHp ? missingHpFrac(u) : 1), def);
  }
}

/** +specs: 1 − hp/maxHp (0 for a dead or full unit). */
export function missingHpFrac(u: Pick<Unit, 'hp' | 'maxHp' | 'alive'>): number {
  return u.alive && u.maxHp > 0 ? clamp(1 - u.hp / u.maxHp, 0, 1) : 0;
}

/** +specs: summed leechPct of a unit's auras (fraction of damage dealt healed back). */
export function leechFrac(ctx: SimContext, u: Unit): number {
  let v = 0;
  forEachMod(ctx, u, (m, stacks) => {
    if (m.stat === 'leechPct') v += m.value * stacks;
  });
  return Math.max(0, v);
}

/** +specs: the unit carries an aura with this flag. */
export function hasAuraFlag(ctx: Pick<SimContext, 'content'>, u: Unit, flag: 'cannotDie' | 'immuneSlow'): boolean {
  for (const inst of u.auras) if (ctx.content.aura(inst.auraId)?.flags?.[flag]) return true;
  return false;
}

/** +basekits: product of (1 + v) over the unit's maxHpPct mods (Hearthward). */
export function maxHpMult(ctx: Pick<SimContext, 'content'>, u: Unit): number {
  let m = 1;
  for (const inst of u.auras) {
    const def = ctx.content.aura(inst.auraId);
    if (!def?.mods) continue;
    for (const mod of def.mods) if (mod.stat === 'maxHpPct') m *= Math.max(0.01, 1 + mod.value * (mod.ignoreStacks ? 1 : inst.stacks));
  }
  return m;
}

/** +basekits: rescale max HP by `ratio` (a maxHpPct aura came or went); current HP moves by the same amount. */
export function rescaleMaxHp(u: Unit, ratio: number): void {
  if (!(ratio > 0) || ratio === 1) return;
  const before = u.maxHp;
  u.maxHp = Math.max(1, Math.round(before * ratio));
  if (u.alive) u.hp = Math.max(1, Math.min(u.maxHp, u.hp + (u.maxHp - before)));
}

export function unitEffectiveStats(ctx: SimContext, u: Unit): EffectiveStats {
  const s = { ...u.stats };
  let powerMult = 1;
  let move = 1;
  let dmgDone = 1;
  let dmgTaken = 1;
  let healDone = 1;
  let healTaken = 1;
  let threat = 1;
  const noSlow = hasAuraFlag(ctx, u, 'immuneSlow'); // +specs: Unstoppable
  forEachMod(ctx, u, (m, stacks) => {
    const v = m.value * stacks;
    switch (m.stat) {
      case 'damageTakenPct':
        if (!m.school) dmgTaken *= 1 + v;
        break;
      case 'damageDonePct':
        if (!m.school && !m.abilityIds) dmgDone *= 1 + v;
        break;
      case 'healingDonePct':
        if (!m.abilityIds) healDone *= 1 + v;
        break;
      case 'healingTakenPct':
        healTaken *= 1 + v;
        break;
      case 'hastePct':
        s.haste += v;
        break;
      case 'critPct':
        s.crit += v;
        break;
      case 'masteryPct':
        s.mastery += v;
        break;
      case 'powerPct':
        powerMult *= 1 + v;
        break;
      case 'moveSpeedPct':
        if (!(noSlow && v < 0)) move *= 1 + v;
        break;
      case 'threatPct':
        threat *= 1 + v;
        break;
      case 'dodge':
      case 'parry':
      case 'block':
      case 'armor':
        s[m.stat] += v;
        break;
      default:
        break; // per-ability kinds (castTimePct, cooldownRatePct, ...) are read by their consumers
    }
  });
  const cc = activeCc(ctx, u);
  const immobile = cc.some((c) => HARD_CC.has(c) || c === 'root');
  s.power *= Math.max(0, powerMult);
  s.armor = clamp(s.armor, 0, 0.85);
  return {
    ...s,
    moveSpeed: immobile ? 0 : Math.max(0, u.moveSpeed * Math.max(0, move)),
    damageDoneMult: Math.max(0, dmgDone),
    damageTakenMult: Math.max(0, dmgTaken),
    healingDoneMult: Math.max(0, healDone),
    healingTakenMult: Math.max(0, healTaken),
    threatMult: Math.max(0, threat),
  };
}

export function effectiveStats(ctx: SimContext, unitId: UnitId): EffectiveStats {
  const u = unitOf(ctx, unitId);
  if (!u) return { ...ZERO_STATS, moveSpeed: 0, damageDoneMult: 1, damageTakenMult: 1, healingDoneMult: 1, healingTakenMult: 1, threatMult: 1 };
  return unitEffectiveStats(ctx, u);
}

/** Outgoing damage multiplier for a school/ability from auras (versatility and keystone scaling excluded). */
export function damageDoneMult(ctx: SimContext, u: Unit, school: School, abilityId: AbilityId): number {
  let mult = 1;
  forEachMod(ctx, u, (m, stacks) => {
    if (m.stat !== 'damageDonePct') return;
    if (m.school && m.school !== school) return;
    if (m.abilityIds && !m.abilityIds.includes(abilityId)) return;
    mult *= 1 + m.value * stacks;
  });
  return Math.max(0, mult);
}

/** Incoming damage multiplier for a school from auras (armor and versatility excluded). */
export function damageTakenMult(ctx: SimContext, u: Unit, school: School): number {
  let mult = 1;
  forEachMod(ctx, u, (m, stacks) => {
    if (m.stat !== 'damageTakenPct') return;
    if (m.school && m.school !== school) return;
    mult *= 1 + m.value * stacks;
  });
  return Math.max(0, mult);
}

export function healingDoneMult(ctx: SimContext, u: Unit, abilityId: AbilityId): number {
  let mult = 1;
  forEachMod(ctx, u, (m, stacks) => {
    if (m.stat !== 'healingDonePct') return;
    if (m.abilityIds && !m.abilityIds.includes(abilityId)) return;
    mult *= 1 + m.value * stacks;
  });
  return Math.max(0, mult);
}

/** Crit chance for an ability: effective crit + matching critChanceBonus mods. */
export function critChanceFor(ctx: SimContext, u: Unit, abilityId: AbilityId): number {
  let c = unitEffectiveStats(ctx, u).crit;
  forEachMod(ctx, u, (m, stacks) => {
    if (m.stat !== 'critChanceBonus') return;
    if (m.abilityIds && !m.abilityIds.includes(abilityId)) return;
    c += m.value * stacks;
  });
  return c;
}

/** Distinct CC types on a unit, in aura order. */
export function activeCc(ctx: SimContext, u: Unit): CcType[] {
  const out: CcType[] = [];
  for (const inst of u.auras) {
    const cc = ctx.content.aura(inst.auraId)?.cc;
    if (cc && !out.includes(cc)) out.push(cc);
  }
  return out;
}

export function controlState(ctx: SimContext, unitId: UnitId): ControlState {
  const u = unitOf(ctx, unitId);
  if (!u || !u.alive) return { canMove: false, canCast: false, canAttack: false, canAct: false, immuneDamage: false, immuneCc: false, cc: [] };
  const cc = activeCc(ctx, u);
  // Evading NPCs (walking home after a reset) are immune to everything (TrinityCore EVADE_MODE).
  const evading = u.ai?.mode === 'evading';
  let immuneDamage = evading;
  let immuneCc = u.ccImmune || evading;
  for (const inst of u.auras) {
    const flags = ctx.content.aura(inst.auraId)?.flags;
    if (flags?.immuneDamage) immuneDamage = true;
    if (flags?.immuneCc) immuneCc = true;
  }
  const hard = cc.some((c) => HARD_CC.has(c));
  const forced = u.move?.mode === 'forced' && u.move.until > ctx.now;
  return {
    canMove: !hard && !forced && !cc.includes('root'),
    canCast: !hard && !forced && !cc.includes('silence'),
    canAttack: !hard && !forced,
    canAct: !hard && !forced,
    immuneDamage,
    immuneCc,
    cc,
  };
}
